import { buildQuantityValueOverrideCount } from '@shared/helpers/element-quantity-value-override.helpers';
import {
  elementForProposalQuantityProjectDiff,
  getQuantityExpressionPropertyForEdit,
  getSelectedElementQuantityProperty,
  isElementQuantityName,
} from '@shared/helpers/element-quantity.helpers';
import { enrichProject } from '@shared/helpers/project-pipeline.helpers';
import {
  propertyChangesToProjectChanges,
  quantityChangesToProjectChanges,
} from '@shared/helpers/pipeline/property-change-to-project-change.helpers';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import {
  getElementById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import type { BuildProposalAppendActionPatchOptions } from './apply-changes-to-proposal.helpers';
import { applyProjectChangesWithProposalContext } from './apply-project-changes-with-proposal-context.helpers';
import type {
  ExpressionValue,
  Baseline,
  IElement,
  Project,
} from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';
import type {
  ElementQuantityExpressionName,
  ElementQuantityName,
} from '@shared/models/element-quantities.model';
import type { ResolvedVersionEnrichContext } from './resolved-version-enrich-cache.helpers';
import { genericProductsLookup } from '@shared/generic-products';
import { guards } from '@shared/helpers/typeguards.helpers';
import type { Recipe } from '@shared/models/recipe.model';

/** Enrich inputs mirrored from {@link ElementListRowConnected} `handleQtySave` / {@link useApplyProjectChanges}. */
export type ElementListRowQuantitySaveEnrichContext =
  ResolvedVersionEnrichContext & {
    recipes?: readonly Recipe[];
  };

export type BuildElementListRowQuantitySaveParams = {
  /** Proposal-resolved row the user sees in the list (same as `displayElement` in the row). */
  displayElement: IElement;
  /** Baseline tree element id (row id), used for proposal append display hints. */
  elementId: string;
  /**
   * Parsed quantity override from the inline editor (e.g. `33`), or `undefined`
   * to clear the override. The string→number parse happens at the input boundary.
   */
  value: number | undefined;
  selectedProposalId?: ProposalId;
  /** Baseline version; required when no proposal is active. */
  baselineVersion?: Baseline;
  selectedQuantityName?: ElementQuantityExpressionName;
  enrichContext?: ElementListRowQuantitySaveEnrichContext;
};

export type BuildElementListRowQuantitySaveResult = {
  changes: ProjectChange[];
  proposalAppend?: BuildProposalAppendActionPatchOptions;
};

/**
 * Resolves the element snapshot used to diff quantity edits (mirrors
 * {@link ElementListRowConnected} `elementForQuantityProjectDiff`).
 */
export const resolveElementForQuantityProjectDiff = (
  displayElement: IElement,
  elementId: string,
  selectedProposalId: ProposalId | undefined,
  baselineVersion: Baseline | undefined,
  quantityName: ElementQuantityName | undefined,
  options?: {
    /**
     * Diff the proposal row with `count.value` kept. Clearing a manual override must
     * see the value the user sees. The baseline copy does not have a proposal-only
     * override, and {@link elementForProposalQuantityProjectDiff} drops a value that
     * mirrors `resolved` (DEV-3951).
     */
    preserveCountValue?: boolean;
  },
): IElement => {
  if (!isElement(displayElement)) return displayElement;
  const resolvedQuantityName =
    quantityName ??
    displayElement.selectedQuantity ??
    getSelectedElementQuantityProperty(displayElement)?.name;
  if (selectedProposalId && isElementQuantityName(resolvedQuantityName)) {
    // A proposal-only override is not on the baseline. Clearing it has to diff
    // the row the user sees, value included. The strip below would hide that clear.
    if (options?.preserveCountValue) return displayElement;
    return elementForProposalQuantityProjectDiff(
      displayElement,
      resolvedQuantityName,
    );
  }
  if (!baselineVersion) return displayElement;
  const baselineElement = getElementById(baselineVersion, elementId, false);
  return isElement(baselineElement) ? baselineElement : displayElement;
};

/**
 * Builds {@link ProjectChange}[] and optional proposal-append options from an
 * Element List row quantity save (mirrors {@link ElementListRowConnected} `handleQtySave`).
 */
export const buildElementListRowQuantitySaveChanges = ({
  displayElement,
  elementId,
  value,
  selectedProposalId,
  baselineVersion,
  selectedQuantityName,
  enrichContext,
}: BuildElementListRowQuantitySaveParams): BuildElementListRowQuantitySaveResult => {
  const quantityPropertyForEdit = getQuantityExpressionPropertyForEdit(
    displayElement,
    selectedQuantityName ??
      (displayElement.selectedQuantity as
        ElementQuantityExpressionName | undefined),
  );
  if (!quantityPropertyForEdit) {
    return { changes: [] };
  }

  const valueOverride = value;

  const currentCount = quantityPropertyForEdit.count;
  const fallbackCount = quantityPropertyForEdit.fallbackCount;
  const baseExpression = currentCount?.expression ?? fallbackCount?.expression;

  const isTriviallyClearable =
    valueOverride === undefined &&
    fallbackCount !== undefined &&
    (!baseExpression || baseExpression === '0');

  let modified: { count: ExpressionValue | undefined };
  if (isTriviallyClearable) {
    modified = { count: undefined };
  } else if (valueOverride === undefined && currentCount) {
    modified = { count: { ...currentCount, value: undefined } };
  } else if (valueOverride !== undefined) {
    // Keep inherit/formula `resolved` on the patch so re-save does not emit
    // ElementQuantityResolved when `count.value` already matches the override (DEV-3313).
    modified = {
      count: buildQuantityValueOverrideCount(
        quantityPropertyForEdit,
        valueOverride,
      ),
    };
  } else {
    modified = { count: undefined };
  }

  const quantityPatch = {
    name: quantityPropertyForEdit.name,
    ...modified,
  };

  // A manual override whose resolved number equals the override is stripped before
  // proposal diff. Clearing it has to see the original value or the clear is a no-op.
  const clearingManualOverride =
    valueOverride === undefined &&
    !isTriviallyClearable &&
    guards.is.finiteNumber(currentCount?.value);

  const diffElement = resolveElementForQuantityProjectDiff(
    displayElement,
    elementId,
    selectedProposalId,
    baselineVersion,
    quantityPropertyForEdit.name,
    { preserveCountValue: clearingManualOverride },
  );

  let changes = propertyChangesToProjectChanges(diffElement, [quantityPatch]);
  if (changes.length === 0) {
    changes = quantityChangesToProjectChanges(diffElement, [quantityPatch]);
  }

  if (changes.length === 0) {
    return { changes: [] };
  }

  const proposalAppend =
    selectedProposalId &&
    isElementQuantityName(quantityPropertyForEdit.name) &&
    enrichContext
      ? {
          enrichContext: {
            project: enrichContext.project,
            weatherHours: enrichContext.weatherHours ?? [],
            dataTables: enrichContext.dataTables ?? [],
          },
          displayHints: [
            {
              elementId,
              quantityName: quantityPropertyForEdit.name,
              displayResolved:
                quantityPropertyForEdit.count?.resolved ??
                quantityPropertyForEdit.fallbackCount?.resolved,
            },
          ],
        }
      : undefined;

  return { changes, proposalAppend };
};

export type BuildQuantityExpressionSaveParams = {
  /** Proposal-resolved element the edit is diffed against. */
  displayElement: IElement;
  /** Baseline tree element id (row id), used for proposal append display hints. */
  elementId: string;
  quantityName: ElementQuantityExpressionName;
  /** New formula to store on the quantity. Omit to keep the current expression. */
  expression?: string;
  /**
   * Numeric override on top of the expression: a number sets it, `null` clears it.
   * Omitting it leaves an existing override untouched, except when `expression` is
   * also set — a new formula always clears the override.
   */
  value?: number | null;
  selectedProposalId?: ProposalId;
  /** Baseline version; required when no proposal is active. */
  baselineVersion?: Baseline;
};

/**
 * Builds {@link ProjectChange}[] for a quantity edit that sets the formula, the numeric
 * override, or both.
 *
 * - `expression` replaces the formula the quantity is calculated from.
 * - `value` sets the manual override that wins over the formula. `null` clears it;
 *   omitting it leaves an existing override in place.
 * - Setting `expression` on its own always clears an active override — pass `value` in
 *   the same call to keep one.
 *
 * Returns an empty array when the quantity is not editable on `displayElement`, or when
 * the edit resolves to no change.
 *
 * @see buildElementListRowQuantitySaveChanges for the inline Element List editor, which
 * only ever writes a numeric override.
 */
export const buildQuantityExpressionSaveChanges = ({
  displayElement,
  elementId,
  quantityName,
  expression,
  value,
  selectedProposalId,
  baselineVersion,
}: BuildQuantityExpressionSaveParams): ProjectChange[] => {
  const quantityPropertyForEdit = getQuantityExpressionPropertyForEdit(
    displayElement,
    quantityName,
  );
  if (!quantityPropertyForEdit) {
    return [];
  }

  const currentCount = quantityPropertyForEdit.count;

  let count: ExpressionValue | Partial<ExpressionValue> | undefined;

  if (typeof value === 'number') {
    // buildQuantityValueOverrideCount rewrites a non-literal formula to the default literal
    // line so inherit text is never copied onto `count`. A formula supplied in the same call
    // is the caller's explicit intent, so it is re-applied on top of that rewrite.
    const overrideCount = buildQuantityValueOverrideCount(
      quantityPropertyForEdit,
      value,
    );
    count =
      expression === undefined
        ? overrideCount
        : { ...overrideCount, expression };
  } else if (expression !== undefined) {
    // A new formula always drops an active override, so callers get one rule instead of
    // the engine's history-dependent one: elementQuantityExpressionToChange bootstraps
    // `count` from `fallbackCount` (losing `value`) on rows that have a fallback, but
    // keeps `value` on rows that do not. Carrying `value: undefined` on the patch makes
    // both paths clear it. Pass `value` alongside `expression` to keep an override.
    count = { ...(currentCount ?? {}), expression, value: undefined };
  } else if (value === null) {
    // Clearing the override keeps the formula. Rows with no explicit `count` of their own
    // have no override to clear, so `count` is dropped, as the inline editor does.
    count = currentCount ? { ...currentCount, value: undefined } : undefined;
  } else {
    return [];
  }

  const quantityPatch = {
    name: quantityPropertyForEdit.name,
    count,
  };

  const clearingManualOverride =
    value === null && guards.is.finiteNumber(currentCount?.value);

  const diffElement = resolveElementForQuantityProjectDiff(
    displayElement,
    elementId,
    selectedProposalId,
    baselineVersion,
    quantityPropertyForEdit.name,
    { preserveCountValue: clearingManualOverride },
  );

  let changes = propertyChangesToProjectChanges(diffElement, [quantityPatch]);
  if (changes.length === 0) {
    changes = quantityChangesToProjectChanges(diffElement, [quantityPatch]);
  }

  return changes;
};

export type ApplyElementListRowQuantitySaveWithEnrichParams =
  BuildElementListRowQuantitySaveParams & {
    project: Project;
    selectedProposalId: ProposalId;
    enrichContext: ElementListRowQuantitySaveEnrichContext;
  };

export type ApplyElementListRowQuantitySaveWithEnrichResult = {
  project: Project;
  changes: ProjectChange[];
};

/**
 * Applies quantity save changes with proposal context, then runs {@link enrichProject}.
 * Used by unit tests; production Element List rows call
 * {@link buildElementListRowQuantitySaveChanges} and apply via the client store hook.
 */
export const applyElementListRowQuantitySaveWithEnrich = ({
  project,
  selectedProposalId,
  enrichContext,
  ...buildParams
}: ApplyElementListRowQuantitySaveWithEnrichParams): ApplyElementListRowQuantitySaveWithEnrichResult => {
  const { changes, proposalAppend } = buildElementListRowQuantitySaveChanges({
    ...buildParams,
    selectedProposalId,
    enrichContext,
  });

  if (changes.length === 0) {
    return { project, changes: [] };
  }

  const updatedProject = applyProjectChangesWithProposalContext(
    project,
    selectedProposalId,
    changes,
    proposalAppend,
  );

  const enrichedProject = enrichProject(updatedProject, {
    prevProject: project,
    productRecord: genericProductsLookup,
    recipes: [...(enrichContext.recipes ?? [])],
    weatherHours: enrichContext.weatherHours ?? [],
    dataTables: enrichContext.dataTables ?? [],
    appliedChanges: changes,
    enrichSelectedProposalId: selectedProposalId,
  });

  return { project: enrichedProject, changes };
};
